import { AttendanceLogFilters, AttendancePunctuality, AttendanceWing } from '../models';

/** `0h 1m 37s` — minutes are unpadded, seconds are two digits. */
export function formatDelay(totalSeconds: number): string {
  const safe = Math.max(0, Math.floor(totalSeconds));
  const hours = Math.floor(safe / 3600);
  const minutes = Math.floor((safe % 3600) / 60);
  const seconds = String(safe % 60).padStart(2, '0');
  return `${hours}h ${minutes}m ${seconds}s`;
}

/** Two words give both initials (`Roshith P` is `RP`); one word or three or more give the first letter only. */
export function initialsOf(name: string): string {
  const words = name.trim().split(/\s+/).filter(Boolean);
  const letters =
    words.length === 2 ? words.map((word) => word[0]).join('') : (words[0]?.[0] ?? '');
  return letters.toUpperCase();
}

/** Filter groups with at least one selection. */
export function countActiveFilters(filters: AttendanceLogFilters): number {
  return Object.values(filters).filter((values: readonly unknown[]) => values.length > 0).length;
}

/** Local `yyyy-MM-dd`, the format the list query uses for its day. */
export function toIsoDate(date: Date): string {
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${date.getFullYear()}-${month}-${day}`;
}

/** The shift's name, `Unassigned` for a Rotor Wing day without one, or `-` for Fixed Wing, which has no shifts. */
export function shiftLabel(wing: AttendanceWing, shiftName: string | null): string {
  if (shiftName) {
    return shiftName;
  }
  return wing === 'ROTOR_WING' ? 'Unassigned' : '-';
}

/**
 * `09:31 AM` from the clock time written in an ISO timestamp, or `-` when there is none. The API
 * already returns the time in the zone the request named, so it is read as written: parsing it as
 * a `Date` would shift it into the browser's zone.
 */
export function formatClockTime(iso: string | null): string {
  const match = /T(\d{2}):(\d{2})/.exec(iso ?? '');
  if (!match) {
    return '-';
  }
  const hours = Number(match[1]);
  const period = hours < 12 ? 'AM' : 'PM';
  return `${String(hours % 12 || 12).padStart(2, '0')}:${match[2]} ${period}`;
}

/** `On time`, a formatted delay, or `-` when there is no status to report. */
export function delayLabel(status: AttendancePunctuality | null, seconds: number | null): string {
  if (status === 'ON_TIME') {
    return 'On time';
  }
  return status === 'LATE' ? formatDelay(seconds ?? 0) : '-';
}

/** First day of the month of a `yyyy-MM-dd` date, in the same format. */
export function monthStartOf(isoDate: string): string {
  return `${isoDate.slice(0, 7)}-01`;
}

/** A `yyyy-MM-dd` string as a local date; `new Date('2026-09-11')` would be read as UTC. */
export function fromIsoDate(isoDate: string): Date {
  const [year, month, day] = isoDate.split('-').map(Number);
  return new Date(year, month - 1, day);
}

/** `Showing 1–7 of 37 crew members`; empty when there is nothing to count. */
export function showingLabel(
  page: number,
  pageSize: number,
  shown: number,
  total: number,
  noun: string
): string {
  if (total <= 0 || shown <= 0) {
    return '';
  }
  const first = page * pageSize + 1;
  return `Showing ${first}–${first + shown - 1} of ${total} ${noun}`;
}
